import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { SHAPE_DATA_FACT, SHAPE_FACTSHEET_BLUEPRINT } from '../../shapes'
import './styles.scss'
import Checkbox from '../../../Form/Checkbox'
import Icon from '../../../Icon'
import Button from '../../../Form/Button'
import { withTranslation } from 'react-i18next'
import { getComponentLabel, getTrackingFactLabel } from '../../helper'
import FactInstance from './fact-instance'
import produce from 'immer'
import Modal from '../../../Modal'
import get from 'lodash.get'
import activityTrackingService from '../../../../../services/activityTrackingService'
import { trackingEvent } from '../../../../../constants'
import { FACT_LABEL_HOVER_COLOR, FACT_LABEL_TOOLTIP_DELAY, FACT_LABEL_TOOLTIP_MIN_WIDTH } from '../../constants'
import { environment } from '../../../../../environment'
import { ReactComponent as PlusIcon } from '../../../../../assets/img/icons/plus.svg'

class FactCheckbox extends PureComponent {
  // the following properties are used for the animation on collapsing a fact instance
  rootElementRef = React.createRef()
  scrollEndTimeout = null
  animationTimeout = null

  state = {
    showConfirmModal: false
  }

  componentWillUnmount () {
    // clear all timers that manage the scroll back animation
    clearTimeout(this.scrollEndTimeout)
    clearTimeout(this.animationTimeout)
    // remove the event listener from the window that is used to check and trigger the animation
    window.removeEventListener('scroll', this.debouncedScrollHandler)
  }

  handleScrollEnd = () => {
    // this method is called when the auto scroll back ended based on the debouncedScrollHandler
    window.removeEventListener('scroll', this.debouncedScrollHandler)

    if (!this.rootElementRef.current || !this.isTopLeftEdgeOfElementInViewport(this.rootElementRef.current)) {
      // if the fact-checkbox is not in viewport, we don't need to trigger the animation
      return
    }

    const factCheckboxHighlightClass = 'fact-checkbox-highlight'
    this.rootElementRef.current.classList.add(factCheckboxHighlightClass)

    // trigger the animation on the checkbox and remove the class after the animation is done
    this.animationTimeout = setTimeout(() => {
      this.rootElementRef.current?.classList.remove(factCheckboxHighlightClass)
    }, 500)
  }

  // this handler is attached on the auto scrollback start and checks if the scroll has ended
  // if it has, it triggers the animation
  debouncedScrollHandler = () => {
    clearTimeout(this.scrollEndTimeout)
    this.scrollEndTimeout = setTimeout(this.handleScrollEnd, 150)
  }

  onCheckboxChange = (e) => {
    if (e.target.checked) {
      activityTrackingService.trackEvent(trackingEvent.checkFact, {
        name: getTrackingFactLabel(this.props.blueprint, this.props.factsheetBlueprint)
      })
      this.props.addFactInstance(this.props.blueprint.id, this.getNewFact())
    } else if (this.isRemovalConfirmationRequired()) {
      this.setState({ showConfirmModal: true })
    } else {
      activityTrackingService.trackEvent(trackingEvent.uncheckFact, {
        name: getTrackingFactLabel(this.props.blueprint, this.props.factsheetBlueprint)
      })
      this.props.deleteFact(this.props.blueprint.id)
    }
  }

  onConfirmModalCancel = (e) => {
    e.preventDefault()
    e.stopPropagation()
    this.setState({ showConfirmModal: false })
  }

  onConfirmModalConfirm = () => {
    this.setState({ showConfirmModal: false })
    this.props.deleteFact(this.props.blueprint.id)
  }

  isChecked () {
    return Array.isArray(this.props.data) && this.props.data.length > 0
  }

  isRemovalConfirmationRequired () {
    if (this.props.data.length > 1) {
      return true
    }
    return !!(this.props.data[0] && (this.props.data[0].hasOwnProperty('attributes') && Object.keys(this.props.data[0].attributes).length > 0))

  }

  getNewFact () {
    return {
      'factDefId': this.props.blueprint.id
    }
  }

  onAddNewFactsInstanceClick (multiplicity) {
    if (this.props.data.length + 1 > multiplicity[1]) {
      return
    }
    this.props.addFactInstance(this.props.blueprint.id, this.getNewFact())
  }

  onFactInstanceDeleteClick = (index) => {
    this.props.deleteFactInstance(this.props.blueprint.id, index)
  }

  onSelectedVariantsChange = (selectedVariantIds, factInstanceIndex) => {
    this.props.putFactInstance(
      this.props.blueprint.id, factInstanceIndex,
      produce(this.props.data[factInstanceIndex], draft => {
        draft.appliesTo = selectedVariantIds
      })
    )
  }

  isTopLeftEdgeOfElementInViewport (el) {
    const rect = el.getBoundingClientRect()
    return rect.top >= 0
      && rect.left >= 0
  }

  onAfterFactInstanceCollapse = () => {
    if (!this.rootElementRef.current || this.isTopLeftEdgeOfElementInViewport(this.rootElementRef.current)) {
      return
    }

    // we need a scrollback animation when the fact instance is collapsed
    // we add a listener that checks for when the scroll has ended and starts the animation
    window.addEventListener('scroll', this.debouncedScrollHandler)
    this.rootElementRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  createFactInstances (fact) {
    const multiplicity = get(fact, 'multiplicity', 1)
    let instanceCount = 0
    const maxAllowedInstanceCount = multiplicity[1]
    if (this.props.data) {
      instanceCount = (maxAllowedInstanceCount < this.props.data.length) ? maxAllowedInstanceCount : this.props.data.length
    }
    const instances = Array.from(Array(instanceCount).keys()).map((key) => {
      let validationStatus = undefined
      if (typeof this.props.validationStatus !== 'undefined') {
        validationStatus = this.props.validationStatus.factInstances[key]
      }
      const factInstanceExpanded = this.props.expandedFactInstances && this.props.expandedFactInstances[key] === true
      const keyUsed = `${this.props.blueprint.id}:i${key}`
      if (environment.IS_DEV && window.__LOG_FS_KEYS) {
        console.debug('Factsheet key used (FactCheckbox instance):', keyUsed)
      }
      return (
        <FactInstance
          key={keyUsed} withHeader={instanceCount > 1}
          blueprint={this.props.blueprint}
          data={this.props.data[key]}
          onCollapseChange={this.onFactInstanceCollapseChange}
          factsheetBlueprint={this.props.factsheetBlueprint}
          onFactInstanceDeleteClick={this.onFactInstanceDeleteClick}
          putFactInstanceExpandedStatus={this.props.putFactInstanceExpandedStatus}
          onSelectedVariantsChange={this.onSelectedVariantsChange}
          validationStatus={validationStatus}
          collapsed={!factInstanceExpanded}
          index={key}
          onAfterCollapse={this.onAfterFactInstanceCollapse}
        />
      )
    })
    const factLabel = getComponentLabel(
      this.props.blueprint,
      fact)
    return (
      <div className={'fact-checkbox-instances'}>
        <React.Fragment>
          {instances}
          {
            (instanceCount > 0 && instanceCount + 1 <= maxAllowedInstanceCount) && (
              <div className={'fact-checkbox-add-instance'}>
                <Button
                  classNames={'Button-Light Button-small'}
                  onClick={() => {this.onAddNewFactsInstanceClick(multiplicity)}}
                  variant="tertiary"
                >
                  <div className={'Button-Content'}>
                    <Icon icon={<PlusIcon/>} size={'extra-small'}/>
                    <span className={'fact-checkbox-add-instance-text'}>
                      {this.props.t('ADD_ANOTHER', { 'label': factLabel })}
                    </span>
                  </div>
                </Button>
              </div>
            )
          }
        </React.Fragment>
      </div>
    )
  }

  render () {
    const checked = this.isChecked()
    const factDefId = this.props.blueprint.id
    const fact = this.props.factsheetBlueprint.factTree.facts[factDefId]
    const label = getComponentLabel(
      this.props.blueprint,
      fact)

    return (
      <div className={'fact-checkbox'} data-fact-id={factDefId} ref={this.rootElementRef}>
        <div className={'fact-checkbox-header'}>
          <Checkbox
            onChange={this.onCheckboxChange}
            value={checked}
            label={label}
            labelHoverColor={FACT_LABEL_HOVER_COLOR}
            tooltip={fact.definition}
            tooltipHoverDelay={FACT_LABEL_TOOLTIP_DELAY}
            tooltipMinWidth={FACT_LABEL_TOOLTIP_MIN_WIDTH}
          />
        </div>
        {this.createFactInstances(fact)}
        <Modal show={this.state.showConfirmModal}
               onEscapeKeyDown={this.onConfirmModalCancel}
               onBackdropClick={this.onConfirmModalCancel}>
          <div className={'modal-confirm-removal'}>
            <p className={'modal-message'}>Do you really want to remove this fact with all instances and attributes?</p>
            <Button variant="neutral" align={'right'}
                    onClick={this.onConfirmModalCancel}>{this.props.t('BUTTON_CANCEL')}</Button>
            <Button variant="attention" align={'right'} onClick={this.onConfirmModalConfirm}>{this.props.t('BUTTON_CONFIRM')}</Button>
          </div>
        </Modal>
      </div>
    )
  }
}

FactCheckbox.propTypes = {
  blueprint: PropTypes.shape({ components: PropTypes.array.isRequired }).isRequired,
  factsheetBlueprint: PropTypes.shape(SHAPE_FACTSHEET_BLUEPRINT).isRequired,
  data: PropTypes.arrayOf(PropTypes.shape(SHAPE_DATA_FACT)),
  validationStatus: PropTypes.object,
  deleteFact: PropTypes.func.isRequired,
  addFactInstance: PropTypes.func.isRequired,
  putFactInstance: PropTypes.func.isRequired,
  putFactInstanceExpandedStatus: PropTypes.func.isRequired,
  deleteFactInstance: PropTypes.func.isRequired,
  expandedFactInstances: PropTypes.array,
}
export default withTranslation()(FactCheckbox)
